import type { MediaProbe } from './model';
// QuickTime/ISO BMFF atom layout: https://developer.apple.com/documentation/quicktime-file-format/atoms
const MAX_MOOV = 64 * 1024 * 1024;
const four = (v: DataView, at: number) => String.fromCharCode(...new Uint8Array(v.buffer, v.byteOffset + at, 4));
interface Box { type: string; start: number; data: number; end: number }
function boxes(v: DataView, start: number, end: number): Box[] {
  const out: Box[] = [];
  for (let at = start; at + 8 <= end;) {
    let size = v.getUint32(at); let header = 8;
    if (size === 1) { if (at + 16 > end) break; size = Number(v.getBigUint64(at + 8)); header = 16; }
    if (size === 0) size = end - at;
    if (!Number.isSafeInteger(size) || size < header || at + size > end) break;
    out.push({ type: four(v, at + 4), start: at, data: at + header, end: at + size }); at += size;
  }
  return out;
}
function children(v: DataView, box: Box, type: string) { return boxes(v, box.data, box.end).find(b => b.type === type); }
function duration(v: DataView, b?: Box): number | undefined {
  if (!b) return; const d = b.data; const ver = v.getUint8(d); const scaleAt = d + (ver === 1 ? 20 : 12);
  if (scaleAt + (ver === 1 ? 12 : 8) > b.end) return;
  const scale = v.getUint32(scaleAt); const ticks = ver === 1 ? Number(v.getBigUint64(scaleAt + 4)) : v.getUint32(scaleAt + 4);
  return scale > 0 && ticks > 0 && Number.isFinite(ticks / scale) ? ticks / scale : undefined;
}
export async function probeMedia(file: Blob): Promise<MediaProbe> {
  const result: MediaProbe = {};
  try {
    let moov: ArrayBuffer | undefined;
    for (let at = 0, count = 0; at + 8 <= file.size && count < 100000; count++) {
      const h = new DataView(await file.slice(at, at + 8).arrayBuffer());
      let size = h.getUint32(0); const type = four(h, 4); let header = 8;
      if (size === 1) { const large = new DataView(await file.slice(at + 8, at + 16).arrayBuffer()); size = Number(large.getBigUint64(0)); header = 16; }
      if (size === 0) size = file.size - at;
      if (!Number.isSafeInteger(size) || size < header || at + size > file.size) throw new Error('invalid atom');
      if (type === 'ftyp' && size >= header + 4) { const brand = new DataView(await file.slice(at + header, at + header + 4).arrayBuffer()); result.container = four(brand, 0) === 'qt  ' ? 'mov' : 'mp4'; }
      if (type === 'moov') { if (size > MAX_MOOV) throw new Error('moov limit'); moov = await file.slice(at + header, at + size).arrayBuffer(); break; }
      at += size;
    }
    if (!moov) throw new Error('no moov');
    const v = new DataView(moov); const root: Box = { type: 'moov', start: 0, data: 0, end: v.byteLength };
    result.durationSeconds = duration(v, children(v, root, 'mvhd'));
    let audio = false; let video = false;
    for (const track of boxes(v, 0, v.byteLength).filter(b => b.type === 'trak')) {
      const mdia = children(v, track, 'mdia'); if (!mdia) continue;
      const handler = children(v, mdia, 'hdlr'); if (!handler || handler.data + 12 > handler.end) continue;
      const kind = four(v, handler.data + 8); if (kind !== 'vide' && kind !== 'soun') continue;
      const minf = children(v, mdia, 'minf'); const stbl = minf && children(v, minf, 'stbl'); const stsd = stbl && children(v, stbl, 'stsd');
      const codec = stsd && stsd.data + 16 <= stsd.end && v.getUint32(stsd.data + 4) > 0 ? four(v, stsd.data + 12) : undefined;
      if (kind === 'soun') { audio = true; result.audioCodec = codec; continue; }
      video = true; result.videoCodec = codec;
      const tkhd = children(v, track, 'tkhd');
      if (tkhd && tkhd.end - tkhd.data >= 84) { result.width = v.getUint32(tkhd.end - 8) / 65536; result.height = v.getUint32(tkhd.end - 4) / 65536; }
      const mdhd = children(v, mdia, 'mdhd'); const trackDuration = duration(v, mdhd);
      result.durationSeconds ??= trackDuration;
      const stts = stbl && children(v, stbl, 'stts');
      if (stts && trackDuration && stts.data + 8 <= stts.end) {
        const entries = v.getUint32(stts.data + 4); let samples = 0;
        if (entries <= (stts.end - stts.data - 8) / 8) { for (let i = 0; i < entries; i++) samples += v.getUint32(stts.data + 8 + i * 8); result.frameRate = samples / trackDuration; }
      }
    }
    if (!video) throw new Error('no video track');
    result.hasAudio = audio;
    if (result.durationSeconds) result.bitrate = file.size * 8 / result.durationSeconds;
    return result;
  } catch { return { ...result, error: '이 파일은 내부 정보를 읽지 못했어요. 재생 방식은 확인 필요예요.' }; }
}
